import APIUrls, {
  APIV2RequestDatetimeFormat,
} from '@/config/simpliciti-apis.js'
import moment from 'moment'
import { filterObjectKeysByDate, getNestedValue } from '@/utils/object.js'
import api from '@/api'
import { getAPIV3Pooling } from '@/api'
import searchService from '@/services/search-service'
import * as R from 'ramda'
import store from '@/store'
import { getRelativeFormattedPastDate } from '@/utils/dates'

/**
 *
 * Used by:
 * Location - Main search -> History by Circuit
 * Location - Main search -> History by Vehicle/Driver -> Circuit Tab
 *
 * @param {Number} circuitId [OPTIONAL]
 * @param {String} date Circuit execution start date
 * @returns Normalized circuit execution detail item
 */
export async function getCircuitExecutionDetails({
  circuitId,
  date,
  ...params
} = {}) {
  let m = moment(date)
  date = m.format('YYYY-MM-DD')
  let from = m.format('HH:mm')
  let startRange = searchService.getRangeTimeSelected(date, 'HH:mm').start
  let endRange = searchService.getRangeTimeSelected(date, 'HH:mm').end
  if (from < startRange) {
    from = startRange
  }
  let to = endRange
  circuitId = circuitId ? `&circuit_id=${circuitId}` : ''
  let apiResponse = (
    await api.v2.post(
      `${
        APIUrls.APIV2_HISTORIQUE_BY_CIRCUIT_DATES
      }?dates=${date}&heure_debut=${encodeURI(from)}&heure_fin=${encodeURI(
        to
      )}&infos_gps=false&linestring=false&troncons=true${circuitId}`
    )
  ).data

  let rawItem = getFirstProperty(getFirstProperty(apiResponse))
  let historyItem = getFirstProperty(rawItem.historique)

  //As API doesn't let us filter by execution, sometimes it could bring two executions and we need to filter (i.g location/history by circuit: details)
  if (params.circuitExecutionId && rawItem.historique) {
    Object.keys(rawItem.historique).forEach((circuitExecutionId) => {
      if (
        circuitExecutionId.toString() == params.circuitExecutionId.toString()
      ) {
        historyItem = rawItem.historique[circuitExecutionId]
      }
    })
  }

  let result = normalizeCircuitExecutionDetailsItem({
    ...rawItem,
    ...historyItem,
  })

  result.troncons = (result.troncons || []).sort((a, b) =>
    parseInt(a.id) > parseInt(b.id) ? 1 : -1
  )

  return result
}

/**
 *
 * Used by:
 * Location - Main search -> Real-time by Vehicle/Driver/Circuit
 *
 * Get last executed circuit infos from API.
 * Change introduced by #47822 : filter out items older than client's months param (CNIL compliance)
 * Pass isUnitTest param from updateCircuitExecutionDetails method to make unit test work
 * @warn APIV2 response: Attribute "historique" will always contain one item
 * @param {Object} {vehicleId, isUnitTest}
 * @returns Normalized circuit execution detail item
 */
export async function getLastCircuitExecutionDetails({
  vehicleId,
  isUnitTest = false,
}) {
  console.log('getLastCircuitExecutionDetails', { vehicleId, isUnitTest })

  if (!vehicleId) {
    console.log('getLastCircuitExecutionDetails: no vehicleId provided')
    return {}
  }

  let extraParams = '&troncons=true'
  let apiResponse

  try {
    apiResponse = (
      await api.v2.post(
        `${APIUrls.APIV2_HISTORIQUE_DERNIER_CIRCUIT}?vehicule_id=${vehicleId}&execution_en_cours=true${extraParams}`
      )
    ).data

    console.log('getLastCircuitExecutionDetails apiResponse', { apiResponse })
  } catch (err) {
    console.log('getLastCircuitExecutionDetails error fetching data', {
      message: err.message,
      stack: err.stack,
    })
    return {}
  }

  // Check if apiResponse is null/undefined/empty
  if (!apiResponse || Object.keys(apiResponse).length === 0) {
    console.log('getLastCircuitExecutionDetails: empty apiResponse')
    return {}
  }

  let rawItem

  //Hack to make unit test work
  if (isUnitTest) {
    rawItem = getFirstProperty(getFirstProperty(apiResponse))
  } else {
    // Otherwise, calculate dateFrom and filter apiResponse
    const realTimeNbMonths =
      store.getters['auth/getRealTimeNbMonthsFromClientParams'] || 3 // Default to 3 months if not set
    const dateFrom = moment(
      getRelativeFormattedPastDate('months', realTimeNbMonths)
    ).format('YYYY-MM-DD')

    console.log('getLastCircuitExecutionDetails filtering', {
      realTimeNbMonths,
      dateFrom,
    })

    const filteredApiResponse = filterObjectKeysByDate(apiResponse, dateFrom)

    // Early return if the filtered response is empty
    if (!filteredApiResponse || Object.keys(filteredApiResponse).length === 0) {
      console.log('getLastCircuitExecutionDetails: empty filteredApiResponse')
      return {}
    }

    try {
      rawItem = getFirstProperty(getFirstProperty(filteredApiResponse))
    } catch (err) {
      console.log('getLastCircuitExecutionDetails error getting properties', {
        message: err.message,
        stack: err.stack,
      })
      return {}
    }
  }

  if (!rawItem || !rawItem.historique) {
    console.log('getLastCircuitExecutionDetails: invalid rawItem', { rawItem })
    return {}
  }

  try {
    const result = normalizeCircuitExecutionDetailsItem({
      ...rawItem,
      ...getFirstProperty(rawItem.historique),
    })
    console.log('getLastCircuitExecutionDetails result', { result })
    return result
  } catch (err) {
    console.log('getLastCircuitExecutionDetails error normalizing item', {
      message: err.message,
      stack: err.stack,
    })
    return {}
  }
}

/**
 * Used by:
 * Location - Main search -> Real-time by Vehicle/Driver/Circuit
 * @param {*} object
 * @returns
 */
export function getFirstProperty(object) {
  return (
    (Object.keys(object || {}).length > 0 && object[Object.keys(object)[0]]) ||
    {}
  )
}

/**
 * Transform/Presenter function for circuit execution details item
 *
 * @todo Add mapping (based on V2 format to avoid too much changes)
 *
 * @param {*} item Details item from APIV2 Historique by_circuit_dates or APIV2 dernier_circuit
 * @returns
 */
export function normalizeCircuitExecutionDetailsItem(item) {
  item = {
    //raw response
    ...item,
    //transform fields (use these when possible)
    circuit_nom_court: item.circuit_nom_court,
    circuit_nom_long: item.circuit_nom_long,
    circuit_categorie: item.circuit_categorie,
    circuit_id: item.circuit_id,
    dateFrom: getNestedValue(item, ['dh_exec_circuit_debut']),
    dateTo: getNestedValue(item, ['dh_exec_circuit_fin']),
  }

  return R.omit(['historique'], item)
}

/**
 * @deprecated: Last "last circuit" API already brings troncons attribute if &trocons=1 is supplied (see getLastCircuitExecutionDetails)
 * Circuit execution steps/troncons
 *
 * @param {Number} vehicleId
 * @param {Number} circuitId
 * @param {String} fromDate Circuit execution start date
 * @param {String} toDate Circuit execution end date
 * @returns
 */
export async function getCircuitExecutionSteps({
  vehicleId,
  circuitId,
  fromDate,
  toDate,
}) {
  console.debugVerbose(8, 'getCircuitExecutionSteps', {
    fromDate,
    toDate,
  })
  let fromDateStr = encodeURI(
    moment(fromDate, APIV2RequestDatetimeFormat).format(
      APIV2RequestDatetimeFormat
    )
  )
  toDate = encodeURI(
    moment(toDate, APIV2RequestDatetimeFormat).format(
      APIV2RequestDatetimeFormat
    )
  )
  let data = (
    await api.v2.post(
      `${APIUrls.APIV2_HISTORIQUE_CIRCUIT_TRONCONS}?dateheure_debut=${fromDateStr}&dateheure_fin=${toDate}&vehicule_id=${vehicleId}&circuit_id=${circuitId}`
    )
  ).data
  return data.length >= 1 ? data[0] : {}
}

export async function fetchCircuitCategories(transform = null) {
  return await getAPIV3Pooling({
    uri: `${APIUrls.APIV3_ROUND_CATEGORIES}?page=1&itemsPerPage=250&order[label]=asc`,
    transform: transform,
  })
}

/**
 * @warn Steven: transform/normalization should be not extracted from circuit-service. If you need two differents normalization, instead, split into two different functions: fetchCircuitsV3/fetchCircuitsV2
 * @todo SearchModule: Move the normalization from search_module into this function (default normalization)
 * @param {*} transform
 * @returns
 */
export async function fetchCircuits(transform = null) {
  return await getAPIV3Pooling({
    uri: `${APIUrls.APIV3_ROUNDS}?page=1&itemsPerPage=250&order[shortName]=asc`,
    transform: transform,
  })
}

export function normalizeRoundCategoryToV2(c) {
  const parentId = getNestedValue(c, 'parentId')
  let result = {
    id: getNestedValue(c, 'id'),
    libelle: getNestedValue(c, 'label'),
    parent_id: parentId == '' ? null : parentId,
  }

  return result
}

export function normalizeRoundToV2(c, categoryList) {
  let categoryId = null

  if (!c?._links?.category?.href) {
    //tries to set "sans category" category
    categoryId = (
      categoryList.find(
        (ctg) => ctg.libelle.toLowerCase().indexOf('sans ') !== -1
      ) || {}
    ).id
  } else {
    categoryId = getNestedValue(c, '_links.category.href').match(/\d+/)[0]
  }

  const category = categoryList.find((c) => c.id == categoryId)
  let result = {
    id: getNestedValue(c, 'id'),
    circuit_id: getNestedValue(c, 'id'),
    nom_court: getNestedValue(c, 'shortName'),
    category_id: categoryId,
    categorie_nom: category?.libelle,
  }

  if (category?.parent_id) {
    const parentCategory =
      categoryList.find((c) => c.id === category.parent_id) || null
    result = {
      ...result,
      parentCategoryName: parentCategory ? parentCategory.libelle : '',
    }
  }

  return result
}

/**
 * Returns circuitExecution details
 * @param circuitId
 * @param circuitExecutionId
 * @param dates An array of dates, allows to get circuit execution details troncons for several days
 * @param getDetailsFn
 * @returns {Promise<any>}
 */
export async function getCircuitExecutionInfos({
  circuitId,
  circuitExecutionId,
  dates,
  getDetailsFn,
}) {
  if (!Array.isArray(dates) || dates.length === 0) {
    throw new Error('dates must be a non-empty array')
  }

  if (typeof getDetailsFn !== 'function') {
    throw new Error('getDetailsFn must be a function')
  }

  let responses
  try {
    // Fetch details for each date
    responses = await Promise.all(
      dates.map((date) =>
        getDetailsFn({
          circuitId,
          date,
          circuitExecutionId,
        })
      )
    )
  } catch (error) {
    throw new Error(
      `Failed fetching circuit execution details: ${error.message}`
    )
  }

  // Filter by circuitExecutionId
  const filteredResponses = responses.filter(
    (resp) => resp.executioncircuit_id == circuitExecutionId
  )

  const startDate = searchService.getRangeTimeSelected(
    dates[0],
    'YYYY-MM-DD HH:mm'
  ).start
  const endDate = searchService.getRangeTimeSelected(
    dates[dates.length - 1],
    'YYYY-MM-DD HH:mm'
  ).end

  // Filter and dedupe troncons
  return filterCircuitDetailsResponses(filteredResponses, startDate, endDate)
}

export function filterCircuitDetailsResponses(responses, startDate, endDate) {
  if (!Array.isArray(responses)) {
    throw new Error('responses must be an array')
  }

  return responses.reduce((acc, obj) => {
    for (const key in obj) {
      if (key === 'troncons' && Array.isArray(obj.troncons)) {
        const existing = acc.troncons || []
        const combined = [...existing, ...obj.troncons]

        // Deduplicate troncons by ID
        const seen = new Map()
        for (const troncon of combined) {
          if (troncon && troncon.id) {
            seen.set(troncon.id, troncon)
          }
        }

        //Filter troncons by date
        acc.troncons = Array.from(seen.values()).filter((troncon) => {
          const tronconStart = moment(troncon.dateheure_debut)
          const tronconEnd = moment(troncon.dateheure_fin)
          return (
            tronconStart.isSameOrAfter(startDate) &&
            tronconEnd.isSameOrBefore(endDate)
          )
        })
      } else {
        acc[key] = acc[key] ?? obj[key]
      }
    }
    return acc
  }, {})
}

export default {
  getLastCircuitExecutionDetails,
  getCircuitExecutionDetails,
  getCircuitExecutionSteps,
  fetchCircuitCategories,
  fetchCircuits,
  normalizeRoundCategoryToV2,
  normalizeRoundToV2,
  getCircuitExecutionInfos,
  filterCircuitDetailsResponses,
}
